import AddRounded from '@mui/icons-material/AddRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import ListSubheader from '@mui/material/ListSubheader';
import type { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { LicenceResponse } from '@shared/schemas';
import { useMemo, useState } from 'react';

import { softFieldSx } from '@/shared/ui/soft-field.styles';

import { filterLicencesByName, licenceDetailsLine } from '../domain/licence-picker';

/** The last entry of the list: not a licence, the way to make one (#593). */
const CREATE_OPTION = { kind: 'create' } as const;
const CREATE_LABEL = 'Create a new licence';
const EXISTING_GROUP = 'Existing licences';

type PickerOption = LicenceResponse | typeof CREATE_OPTION;

const isCreate = (option: PickerOption): option is typeof CREATE_OPTION => option === CREATE_OPTION;

export interface LicencePickerViewProps {
  id: string;
  /** The licences on offer; already without the deactivated ones. */
  licences: readonly LicenceResponse[];
  /** The picked licence id; `''` = none. */
  value: string;
  onChange: (licenceId: string) => void;
  /**
   * Adds "Create a new licence" at the bottom of the list, whatever the search;
   * picking it calls this instead of changing the value.
   */
  onCreateRequested?: () => void;
  error?: string;
  /**
   * `soft`: the redesign's filled field, its label rendered above by the host
   * (`FieldLabel htmlFor={id}`), with a key icon inside (New List, #595). The
   * licence's status and lists are told by "Where you are". Default: Add user's
   * outlined field with its floating label.
   */
  appearance?: 'outlined' | 'soft';
}

const groupLabelSx: SxProps<Theme> = {
  top: -8,
  lineHeight: '28px',
  fontSize: '0.68rem',
  fontWeight: 600,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
};
const groupListSx: SxProps<Theme> = { p: 0 };
const licenceOptionSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', minWidth: 0 };
const createOptionSx: SxProps<Theme> = {
  display: 'flex',
  gap: 1,
  color: 'primary.main',
  fontWeight: 600,
  borderTop: (t) => `1px solid ${t.palette.divider}`,
};

// The dialogs' soft field, with the Autocomplete's own paddings evened out.
const softPickerSx: SxProps<Theme> = [
  softFieldSx,
  { '& .MuiFilledInput-root.MuiInputBase-hiddenLabel': { py: 0.5, pl: 1.5 } },
];

/**
 * The Add user Licence field (#592): a searchable list of the licences, each
 * with its `type • country • N users` line under its name, grouped under
 * "Existing licences": typing narrows them by name.
 *
 * The input text is controlled here because the create entry is an action, not
 * a value: MUI would otherwise write its label into the input when it is picked.
 */
export default function LicencePickerView({
  id,
  licences,
  value,
  onChange,
  onCreateRequested,
  error,
  appearance = 'outlined',
}: LicencePickerViewProps) {
  const selected = useMemo(() => licences.find((l) => l.id === value) ?? null, [licences, value]);
  const [inputValue, setInputValue] = useState('');

  const options = useMemo<PickerOption[]>(
    () => (onCreateRequested ? [...licences, CREATE_OPTION] : [...licences]),
    [licences, onCreateRequested],
  );

  return (
    <Autocomplete<PickerOption>
      id={id}
      options={options}
      value={selected}
      onChange={(_, next) => {
        if (next && isCreate(next)) {
          setInputValue(selected?.name ?? '');
          onCreateRequested?.();
          return;
        }
        onChange(next?.id ?? '');
      }}
      inputValue={inputValue}
      onInputChange={(_, next) => setInputValue(next)}
      // The create entry survives every search: it is the answer to "not found".
      filterOptions={(_, { inputValue: search }) => {
        const matches: PickerOption[] = filterLicencesByName(licences, search);
        return onCreateRequested ? [...matches, CREATE_OPTION] : matches;
      }}
      getOptionLabel={(o) => (isCreate(o) ? CREATE_LABEL : o.name)}
      isOptionEqualToValue={(o, v) => !isCreate(o) && !isCreate(v) && o.id === v.id}
      groupBy={(o) => (isCreate(o) ? '' : EXISTING_GROUP)}
      renderGroup={(params) => (
        <li key={params.key}>
          {params.group && (
            <ListSubheader component="div" sx={groupLabelSx}>
              {params.group}
            </ListSubheader>
          )}
          <Box component="ul" sx={groupListSx}>
            {params.children}
          </Box>
        </li>
      )}
      renderOption={({ key, ...props }, option) =>
        isCreate(option) ? (
          <Box component="li" key={key} {...props} sx={createOptionSx}>
            <AddRounded fontSize="small" />
            {CREATE_LABEL}
          </Box>
        ) : (
          <li key={key} {...props}>
            <Box sx={licenceOptionSx}>
              <Typography variant="body2">{option.name}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {licenceDetailsLine(option)}
              </Typography>
            </Box>
          </li>
        )
      }
      autoHighlight
      renderInput={(params) =>
        appearance === 'soft' ? (
          <TextField
            {...params}
            hiddenLabel
            variant="filled"
            placeholder="Search a licence"
            required
            error={Boolean(error)}
            helperText={error}
            slotProps={{
              ...params.slotProps,

              input: {
                ...params.slotProps.input,
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyRounded fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={softPickerSx}
          />
        ) : (
          <TextField
            {...params}
            label="Licence"
            placeholder="Search a licence"
            required
            error={Boolean(error)}
            helperText={error}
          />
        )
      }
    />
  );
}
